import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, InputAdornment, IconButton
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useMutation, gql } from '@apollo/client';

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPasswordWithToken($token: String!, $newPassword: String!) {
    resetPasswordWithToken(token: $token, newPassword: $newPassword) {
      success
      message
    }
  }
`;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD_MUTATION, {
    onCompleted: (data) => {
      if (data.resetPasswordWithToken.success) {
        setSuccessMessage(data.resetPasswordWithToken.message);
        setErrorMessage('');
        // Rediriger vers la page de connexion après quelques secondes
        setTimeout(() => navigate('/login'), 4000);
      } else {
        setErrorMessage(data.resetPasswordWithToken.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage('Erreur: ' + error.message);
      setSuccessMessage('');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!password || password.length < 8) {
      setErrorMessage('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Les mots de passe doivent correspondre');
      return;
    }

    resetPassword({ variables: { token, newPassword: password } });
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Paper elevation={4} sx={{
          p: { xs: 4, md: 5 }, width: '100%', borderRadius: 4,
          border: '1px solid rgba(0,116,129,0.12)',
          boxShadow: '0 8px 40px rgba(0,116,129,0.10)',
        }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 70, height: 70, borderRadius: '50%', mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg, #006d78, #005a63)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <LockResetIcon sx={{ color: '#fff', fontSize: 36 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
              Nouveau mot de passe
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
              Choisissez un nouveau mot de passe pour votre compte
            </Typography>
          </Box>

          {!token ? (
            <Alert severity="error">
              Lien de réinitialisation invalide. Veuillez refaire une demande depuis la page de connexion.
            </Alert>
          ) : (
            <>
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{errorMessage}</Alert>
              )}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  {successMessage} Redirection vers la page de connexion…
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Nouveau mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={Boolean(successMessage)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#006d78', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirmer le mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={Boolean(successMessage)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#006d78', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || Boolean(successMessage)}
                  sx={{
                    mt: 3, mb: 1, py: 1.4, borderRadius: 2, fontWeight: 700,
                    background: 'linear-gradient(135deg, #006d78, #005a63)',
                    '&:hover': { background: 'linear-gradient(135deg, #005a63, #003d45)' },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            </>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button component={RouterLink} to="/login" variant="text" sx={{ color: '#006d78', textTransform: 'none' }}>
              ← Retour à la connexion
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPassword;
